import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { CaretDown, Check, MagnifyingGlass } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildSnapshot(v) {
  const jalanPart = [v.jalan, v.no_rumah ? `No. ${v.no_rumah}` : ""].filter(Boolean).join(" ");
  const rtrw = [v.rt ? `RT ${v.rt}` : "", v.rw ? `RW ${v.rw}` : ""].filter(Boolean).join("/");
  const parts = [
    jalanPart,
    rtrw,
    v.kelurahan_nama ? `Kel. ${v.kelurahan_nama}` : "",
    v.kecamatan_nama ? `Kec. ${v.kecamatan_nama}` : "",
    v.kabupaten_nama || "",
    v.provinsi_nama || "",
    v.kode_pos || "",
  ].filter(Boolean);
  return parts.join(", ");
}

const inpCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#EC4899] focus:ring-2 focus:ring-[#EC4899]/15";
const lbl = "mb-1 block text-xs font-semibold text-slate-500";

// Touch-friendly searchable dropdown (replaces native <select> to avoid the
// iOS Safari native overlay + long-name / cascade persistence issues).
function SearchableSelect({ label, testid, value, valueLabel, options, onSelect, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("touchstart", onDoc); };
  }, [open]);

  const filtered = q ? options.filter((o) => o.nama.toLowerCase().includes(q.toLowerCase())) : options;

  return (
    <div ref={ref} className="relative">
      <label className={lbl}>{label}</label>
      <button
        type="button"
        data-testid={testid}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm outline-none transition-colors ${
          disabled
            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
            : open
            ? "border-[#EC4899] bg-white ring-2 ring-[#EC4899]/15"
            : "border-slate-200 bg-white"
        }`}
      >
        <span className={`min-w-0 flex-1 truncate ${valueLabel ? "text-slate-800" : "text-slate-400"}`}>{valueLabel || placeholder}</span>
        <CaretDown size={16} weight="bold" className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && !disabled && (
        <div className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-100 p-2">
            <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-2">
              <MagnifyingGlass size={16} className="shrink-0 text-slate-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari..."
                data-testid={`${testid}-search`}
                className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
              />
            </div>
          </div>
          <ul className="max-h-56 overflow-y-auto overscroll-contain py-1" data-testid={`${testid}-list`}>
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-slate-400">Tidak ditemukan</li>
            ) : (
              filtered.map((o) => (
                <li key={o.kode}>
                  <button
                    type="button"
                    data-testid={`${testid}-opt-${o.kode}`}
                    onClick={() => { onSelect(o.kode); setOpen(false); setQ(""); }}
                    className={`flex w-full items-start gap-2 px-3 py-3 text-left text-sm transition-colors active:bg-pink-100 ${
                      o.kode === value ? "bg-pink-50 font-semibold text-[#DB2777]" : "text-slate-700 hover:bg-pink-50/60"
                    }`}
                  >
                    <span className="mt-0.5 w-4 shrink-0">{o.kode === value && <Check size={16} weight="bold" className="text-[#DB2777]" />}</span>
                    <span className="min-w-0 break-words">{o.nama}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AddressPicker({ value = {}, onPatch, testidPrefix = "addr", enableGeo = false, required = true }) {
  const rq = required ? " *" : "";
  const [provs, setProvs] = useState([]);
  const [kabs, setKabs] = useState([]);
  const [kecs, setKecs] = useState([]);
  const [kels, setKels] = useState([]);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [geoErr, setGeoErr] = useState("");

  const useCurrentLocation = () => {
    setGeoErr("");
    if (!navigator.geolocation) { setGeoErr("Perangkat tidak mendukung lokasi. Anda tetap bisa lanjut dengan kurir reguler."); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { patch({ latitude: Number(pos.coords.latitude.toFixed(6)), longitude: Number(pos.coords.longitude.toFixed(6)) }); setGpsBusy(false); },
      () => { setGpsBusy(false); setGeoErr("Izin lokasi ditolak atau gagal. Kurir instan tidak ditampilkan; kurir reguler tetap tersedia."); },
      { enableHighAccuracy: true, timeout: 10000 });
  };

  useEffect(() => {
    axios.get(`${API}/public/regions`).then((r) => setProvs(r.data)).catch(() => setProvs([]));
  }, []);

  const fetchChildren = useCallback((parent, setter) => {
    if (!parent) { setter([]); return; }
    axios.get(`${API}/public/regions`, { params: { parent } }).then((r) => setter(r.data)).catch(() => setter([]));
  }, []);

  useEffect(() => { fetchChildren(value.provinsi, setKabs); }, [value.provinsi, fetchChildren]);
  useEffect(() => { fetchChildren(value.kabupaten, setKecs); }, [value.kabupaten, fetchChildren]);
  useEffect(() => { fetchChildren(value.kecamatan, setKels); }, [value.kecamatan, fetchChildren]);

  const patch = (p) => {
    const merged = { ...value, ...p };
    const snap = buildSnapshot(merged);
    onPatch({ ...p, alamat: snap, alamat_data: {
      provinsi: merged.provinsi, provinsi_nama: merged.provinsi_nama,
      kabupaten: merged.kabupaten, kabupaten_nama: merged.kabupaten_nama,
      kecamatan: merged.kecamatan, kecamatan_nama: merged.kecamatan_nama,
      kelurahan: merged.kelurahan, kelurahan_nama: merged.kelurahan_nama,
      jalan: merged.jalan, no_rumah: merged.no_rumah, rt: merged.rt, rw: merged.rw, kode_pos: merged.kode_pos,
      latitude: merged.latitude, longitude: merged.longitude,
    } });
  };

  const onProv = (kode) => {
    const nama = provs.find((x) => x.kode === kode)?.nama || "";
    patch({ provinsi: kode, provinsi_nama: nama, kabupaten: "", kabupaten_nama: "", kecamatan: "", kecamatan_nama: "", kelurahan: "", kelurahan_nama: "" });
  };
  const onKab = (kode) => {
    const nama = kabs.find((x) => x.kode === kode)?.nama || "";
    patch({ kabupaten: kode, kabupaten_nama: nama, kecamatan: "", kecamatan_nama: "", kelurahan: "", kelurahan_nama: "" });
  };
  const onKec = (kode) => {
    const nama = kecs.find((x) => x.kode === kode)?.nama || "";
    patch({ kecamatan: kode, kecamatan_nama: nama, kelurahan: "", kelurahan_nama: "" });
  };
  const onKel = (kode) => {
    const nama = kels.find((x) => x.kode === kode)?.nama || "";
    patch({ kelurahan: kode, kelurahan_nama: nama });
  };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <SearchableSelect label={`Provinsi${rq}`} testid={`${testidPrefix}-provinsi`} value={value.provinsi || ""} valueLabel={value.provinsi_nama} options={provs} onSelect={onProv} placeholder="-- Pilih Provinsi --" />
      <SearchableSelect label={`Kabupaten/Kota${rq}`} testid={`${testidPrefix}-kabupaten`} value={value.kabupaten || ""} valueLabel={value.kabupaten_nama} options={kabs} onSelect={onKab} disabled={!value.provinsi} placeholder="-- Pilih Kabupaten/Kota --" />
      <SearchableSelect label={`Kecamatan${rq}`} testid={`${testidPrefix}-kecamatan`} value={value.kecamatan || ""} valueLabel={value.kecamatan_nama} options={kecs} onSelect={onKec} disabled={!value.kabupaten} placeholder="-- Pilih Kecamatan --" />
      <SearchableSelect label={`Kelurahan/Desa${rq}`} testid={`${testidPrefix}-kelurahan`} value={value.kelurahan || ""} valueLabel={value.kelurahan_nama} options={kels} onSelect={onKel} disabled={!value.kecamatan} placeholder="-- Pilih Kelurahan/Desa --" />
      <div className="sm:col-span-2">
        <label className={lbl}>Nama Jalan / Perumahan{rq}</label>
        <input data-testid={`${testidPrefix}-jalan`} value={value.jalan || ""} onChange={(e) => patch({ jalan: e.target.value })} className={inpCls} placeholder="cth. Jl. Mulawarman / Perum ..." />
      </div>
      <div>
        <label className={lbl}>No. Rumah</label>
        <input data-testid={`${testidPrefix}-norumah`} value={value.no_rumah || ""} onChange={(e) => patch({ no_rumah: e.target.value })} className={inpCls} placeholder="opsional" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><label className={lbl}>RT{rq}</label><input data-testid={`${testidPrefix}-rt`} value={value.rt || ""} onChange={(e) => patch({ rt: e.target.value })} className={inpCls} /></div>
        <div><label className={lbl}>RW</label><input data-testid={`${testidPrefix}-rw`} value={value.rw || ""} onChange={(e) => patch({ rw: e.target.value })} className={inpCls} /></div>
        <div><label className={lbl}>Kode Pos</label><input data-testid={`${testidPrefix}-kodepos`} value={value.kode_pos || ""} onChange={(e) => patch({ kode_pos: e.target.value })} className={inpCls} /></div>
      </div>
      {enableGeo && (
        <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid={`${testidPrefix}-geo`}>
          {(value.latitude != null && value.longitude != null) ? (
            <div className="flex items-center justify-between gap-2">
              <span data-testid={`${testidPrefix}-geo-saved`} className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600"><Check size={15} weight="bold" /> Titik lokasi tersimpan</span>
              <button type="button" data-testid={`${testidPrefix}-geo-update`} onClick={useCurrentLocation} disabled={gpsBusy} className="shrink-0 rounded-lg border border-[#7C4DE0] px-2.5 py-1 text-[11px] font-bold text-[#6A38C7] disabled:opacity-50">{gpsBusy ? "Mengambil…" : "Perbarui lokasi"}</button>
            </div>
          ) : (
            <button type="button" data-testid={`${testidPrefix}-geo-use`} onClick={useCurrentLocation} disabled={gpsBusy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#7C4DE0] px-3 py-2 text-xs font-bold text-white disabled:opacity-50">{gpsBusy ? "Mengambil lokasi…" : "Gunakan Lokasi Saat Ini untuk menampilkan kurir instan"}</button>
          )}
          {geoErr && <p data-testid={`${testidPrefix}-geo-err`} className="mt-1.5 rounded-lg bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-600">{geoErr}</p>}
          <p className="mt-1.5 text-[11px] text-slate-400">Pastikan HP Anda sedang berada di <b>alamat tujuan</b> saat menekan tombol. Jika Anda sedang di tempat lain, jangan pakai lokasi ini. Opsional — kurir reguler tetap tersedia tanpa lokasi.</p>
        </div>
      )}
      {value.alamat && (
        <p data-testid={`${testidPrefix}-snapshot`} className="sm:col-span-2 rounded-lg bg-pink-50 px-3 py-2 text-xs text-[#DB2777]">{value.alamat}</p>
      )}
    </div>
  );
}
